/** Section 1 — every link for the night, plus the one button that kills them. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { btn } from './buttons';

const Row = ({ label, path, testid }) => {
    const url = `${window.location.origin}${path}`;
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</p>
            <div className="flex items-center gap-2">
                <code className="text-xs break-all flex-1 text-slate-700 dark:text-slate-200"
                      data-testid={testid}>{url}</code>
                <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(url); toast.success('Copied'); }}
                    data-testid={`${testid}-copy`}
                    className={`${btn.smallSecondary} shrink-0`}
                >
                    <Copy size={14} />
                </button>
            </div>
        </div>
    );
};

export const GoalLinksBlock = ({ cfg, onReload }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [askReset, setAskReset] = useState(false);
    const [resetOut, setResetOut] = useState(null);

    // Start the demo over, from this page. One press asks, the second one does
    // it — the same two steps as the iPad bar, and the same server action.
    const resetDemo = async () => {
        setBusy(true);
        try {
            const { data } = await api.post('/goal-game/admin/reset-demo',
                                            { organization_id: cfg.organization_id });
            setAskReset(false);
            setResetOut(data);
            await onReload();
            toast.success('Demo reset — the next phone visit starts fresh');
        } catch (_e) {
            toast.error('Could not reset the demo');
        } finally { setBusy(false); }
    };

    const [askPw, setAskPw] = useState(false);
    const [pw, setPw] = useState('');
    const [pwError, setPwError] = useState('');

    // Step one: the warning that already existed. Step two: the account
    // password, checked on the server inside the same call.
    const regenerate = () => {
        if (!window.confirm('Replace the operator and trigger secrets? Every link you have already shared or written to an NFC card stops working immediately.')) return;
        setPw('');
        setPwError('');
        setAskPw(true);
    };

    const regenerateConfirm = async () => {
        setBusy(true);
        setPwError('');
        try {
            await api.post('/goal-game/admin/demo-link/regenerate',
                           { organization_id: cfg.organization_id, password: pw });
            setAskPw(false);
            setPw('');
            await onReload();
            toast.success(t('goalGame.regenDone'));
        } catch (e) {
            const detail = String(e?.response?.data?.detail || '');
            if (detail.startsWith('step_up_locked')) {
                const secs = Number(detail.split(':')[1]) || 300;
                setPwError(t('goalGame.regenLocked', { minutes: Math.max(1, Math.ceil(secs / 60)) }));
            } else if (detail === 'step_up_wrong_password') {
                setPwError(t('goalGame.regenWrongPassword'));
            } else {
                setPwError('Could not regenerate the links');
            }
            setPw('');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-3 pt-3">
            <Row label="Fan link (table card)" path={cfg.fan_path} testid="gg-link-fan" />
            <Row label="Operator link (keep private)" path={cfg.operator_path} testid="gg-link-operator" />
            <Row label="iPad demo link (fan screen with the controls)" path={cfg.ipad_path} testid="gg-link-ipad" />
            {cfg.can_reset_demo && (
                <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-3 space-y-2"
                     data-testid="gg-reset-demo-box">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Start the demo over</p>
                    {!askReset ? (
                        <button type="button" onClick={() => setAskReset(true)} disabled={busy}
                                data-testid="gg-reset-demo" className={btn.secondary}>
                            <RotateCcw size={15} />Reset demo
                        </button>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <button type="button" onClick={resetDemo} disabled={busy}
                                    data-testid="gg-reset-demo-confirm" className={btn.warning}>
                                {busy ? <Loader2 className="animate-spin" size={15} /> : <RotateCcw size={15} />}
                                Yes, reset the demo
                            </button>
                            <button type="button" onClick={() => setAskReset(false)} disabled={busy}
                                    data-testid="gg-reset-demo-cancel" className={btn.secondary}>
                                Cancel
                            </button>
                        </div>
                    )}
                    <p className="text-xs text-slate-500">
                        Clears the open round, every table set-up and PIN, the PIN lock-outs, the
                        scratch plays and the vouchers this game minted, and puts the main
                        promotion back on air. Links, tokens, prizes, odds, design, wording and
                        booked games are untouched. Demo organizations only.
                    </p>
                    <p className="text-xs text-slate-500" data-testid="gg-reset-demo-patron-note">
                        {t('goalGame.resetPatronNote')}
                    </p>
                    {resetOut && (
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400"
                           data-testid="gg-reset-demo-result">
                            Cleared: {resetOut.tables} table set-up(s), {resetOut.plays} play(s),{' '}
                            {resetOut.pin_locks} PIN lock-out(s), {resetOut.vouchers} voucher(s)
                            {resetOut.ended ? ', and the open round was closed' : ''}.
                        </p>
                    )}
                    {resetOut?.patron?.fans !== undefined && (
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400"
                           data-testid="gg-reset-demo-patron-result">
                            {t('goalGame.resetPatronCleared', resetOut.patron)}
                        </p>
                    )}
                </div>
            )}
            <Row label="Goal trigger link (NFC goal card — shows GOAL! and sounds the horn)"
                 path={cfg.trigger_path} testid="gg-link-trigger" />
            <Row label="Silent trigger link (POST, no page — use this in a showroom Action card)"
                 path={cfg.silent_trigger_url} testid="gg-link-silent" />
            <button
                type="button" onClick={regenerate} disabled={busy}
                data-testid="gg-link-regenerate"
                className={btn.warning}
            >
                {busy ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
                Regenerate operator + trigger links
            </button>
            {askPw && (
                <div className="rounded-xl border border-amber-300 dark:border-amber-700 p-3 space-y-2"
                     data-testid="gg-regen-password-box">
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                        {t('goalGame.regenAskPassword')}
                    </p>
                    <input
                        type="password" value={pw} autoComplete="current-password"
                        onChange={(e) => setPw(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && pw) regenerateConfirm(); }}
                        placeholder={t('goalGame.regenPasswordPlaceholder')}
                        data-testid="gg-regen-password"
                        className="w-full min-h-[44px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={regenerateConfirm} disabled={busy || !pw}
                                data-testid="gg-regen-password-confirm" className={btn.warning}>
                            {busy ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
                            {t('goalGame.regenConfirm')}
                        </button>
                        <button type="button" disabled={busy}
                                onClick={() => { setAskPw(false); setPw(''); setPwError(''); }}
                                data-testid="gg-regen-password-cancel" className={btn.secondary}>
                            {t('goalGame.regenCancel')}
                        </button>
                    </div>
                    {pwError && (
                        <p className="text-xs font-semibold text-rose-600 dark:text-rose-400"
                           data-testid="gg-regen-password-error">{pwError}</p>
                    )}
                </div>
            )}
            <p className="text-xs text-slate-500">
                Anyone holding the operator URL can fire a goal — it is a shared secret, not a login.
                The two trigger links use a separate, fire-only secret: they can open a round and
                nothing else. Regenerating replaces both, so every old link stops working at once.
            </p>
        </div>
    );
};

export default GoalLinksBlock;
